import {
  getReferentialsFindAllQueryKey,
  useReferentialsFindAll,
} from '@/api/WhisApi/generated';
import type { ReferentialsResponseDto } from '@/api/WhisApi/generated/model/referentialsResponseDto';
import { useAuth } from '@/hooks/useAuth';
import { useOfflineDegradedMode } from '@/hooks/useOfflineDegradedMode';
import {
  getStoredReferentials,
  saveStoredReferentials,
} from '@/store/referentials/referentials.storage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

interface UseReferentialsDataResult {
  data: ReferentialsResponseDto | undefined;
  isPending: boolean;
}

export const useReferentialsData = (): UseReferentialsDataResult => {
  const { isAuthenticated } = useAuth();
  const { isOfflineDegradedMode } = useOfflineDegradedMode();
  const queryClient = useQueryClient();
  const referentialsQueryKey = getReferentialsFindAllQueryKey();

  const storedReferentialsQuery = useQuery({
    queryKey: ['referentials', 'stored-cache'],
    queryFn: getStoredReferentials,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const referentialsQuery = useReferentialsFindAll({
    query: {
      enabled: isAuthenticated && !isOfflineDegradedMode,
      initialData: () =>
        (queryClient.getQueryData(referentialsQueryKey) ??
          storedReferentialsQuery.data) as ReferentialsResponseDto | undefined,
    },
  });

  useEffect(() => {
    if (!storedReferentialsQuery.data) {
      return;
    }

    const existingReferentials = queryClient.getQueryData(referentialsQueryKey);
    if (existingReferentials) {
      return;
    }

    queryClient.setQueryData(referentialsQueryKey, storedReferentialsQuery.data);
  }, [queryClient, referentialsQueryKey, storedReferentialsQuery.data]);

  useEffect(() => {
    if (!referentialsQuery.data) {
      return;
    }

    void saveStoredReferentials(referentialsQuery.data);
  }, [referentialsQuery.data]);

  const data =
    referentialsQuery.data ??
    (queryClient.getQueryData(referentialsQueryKey) as
      | ReferentialsResponseDto
      | undefined) ??
    (storedReferentialsQuery.data ?? undefined);

  return {
    data,
    isPending:
      storedReferentialsQuery.isPending || (referentialsQuery.isPending && !data),
  };
};
